import { useCallback, useEffect } from 'react';
import { useChatSocketState } from '../../../store/useChatSocketState';
import { ConversationMessageAudience } from '../../../types/conversation.types';

/**
 * The open thread's half of the socket: which room to join, who is typing, and
 * whether the AI assistant is working on the last message.
 *
 * The socket itself lives in `AdminLayout` - so the sidebar badge stays live off
 * the chat page - and this store is how the open thread reaches it. Clearing the
 * observed id on unmount is what unsubscribes.
 *
 * Both directions are no-ops on the merchant side, which has no room to join.
 */
export const useThreadPresence = (conversationId: number | string | null) => {
  const setObservedConversationId = useChatSocketState(
    (state) => state.setObservedConversationId
  );
  const emitTyping = useChatSocketState((state) => state.emitTyping);
  const typingUsers = useChatSocketState((state) => state.typingUsers);
  const assistantState = useChatSocketState((state) => state.assistantState);

  useEffect(() => {
    // Not coerced to a number: the merchant side keys on a string sentinel.
    setObservedConversationId(conversationId);
    return () => setObservedConversationId(null);
  }, [conversationId, setObservedConversationId]);

  /* The audience is a dispatcher thread's: a note being typed must not show
     the customer "vDrive Support is typing". */
  const onTyping = useCallback(
    (typing: boolean, audience?: ConversationMessageAudience) => {
      if (conversationId === null) return;
      emitTyping?.(conversationId, typing, audience);
    },
    [emitTyping, conversationId]
  );

  return { onTyping, typingUsers, assistantState };
};
